"use client";
import React from "react";
import dynamic from "next/dynamic";
import { useAuthModal } from "@/auth/auth-modal-provider";
import { useConvexAuth } from "convex/react";
import { useBoard } from "@/components/BoardProvider";
import Link from "next/link";

// The swirling Percentile Labs globe. render() only outputs a <div> (all WebGL
// is in useEffect), so SSR-ing it lets Next preload the three.js chunk in
// parallel with hydration instead of in a post-hydration waterfall.
const PercentileGlobe = dynamic(
  () => import("@/app/percentile/components/PercentileDonut"),
  { loading: () => null },
);

export function HeroSection() {
  const board = useBoard();
  const { openAuthModal } = useAuthModal();
  const { isAuthenticated } = useConvexAuth();

  const handleSignupClick = React.useCallback(() => {
    openAuthModal("signup");
  }, [openAuthModal]);

  return (
    <div className="flex items-center justify-center mt-10 mb-0 md:mt-24 md:mb-0 px-6">
      <div className="flex max-w-2xl flex-col items-center gap-7 text-center mx-auto">
        {/* Swirling globe stacked above the headline, with the payoff phrase
            underlined in the board's accent. */}
        <div className="flex flex-col items-center gap-4 md:gap-6">
          <div className="relative size-20 shrink-0 md:size-28" aria-hidden="true">
            <PercentileGlobe className="size-full" speed={0.2} />
          </div>
          <h1 className="font-script leading-[1.15] text-foreground text-4xl sm:text-5xl md:text-6xl">
            {board.shortName} revision, built for the{" "}
            <span className="relative whitespace-nowrap">
              top percentile
              <svg
                aria-hidden="true"
                viewBox="0 0 200 12"
                preserveAspectRatio="none"
                className="absolute -bottom-1 left-0 h-2 w-full text-primary md:h-3"
              >
                <path
                  d="M3 8.5C48 3.5 152 3.5 197 7"
                  stroke="currentColor"
                  strokeWidth="4"
                  strokeLinecap="round"
                  fill="none"
                />
              </svg>
            </span>
          </h1>
        </div>

        <p className="max-w-xl text-lg md:text-xl text-foreground leading-relaxed font-polysans font-light">
        1000+ {board.shortName} questions designed to stretch the most capable of students.
        </p>

        <div className="flex flex-col items-center gap-4">
          {isAuthenticated ? (
            <Link
              href="/dashboard"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium text-white rounded-full px-6 py-3 bg-primary hover:bg-primary/80 shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70 font-polysans"
            >
              Go to Dashboard →
            </Link>
          ) : (
            <button
              type="button"
              onClick={handleSignupClick}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium text-white/90 rounded-full px-6 py-3 bg-primary hover:bg-primary/80 shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70 font-polysans"
            >
              Start revising for {board.shortName} →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
